import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react"
import { I18nextProvider } from "react-i18next"

import { createFormatting, type LocalizationFormatters } from "./formatters"
import {
  defaultLocalizationSettings,
  type Localization,
  type LocalizationSettings,
  type LocalizationSnapshot,
} from "./localization"

interface LocalizationContextValue {
  readonly localization: Localization
  readonly snapshot: LocalizationSnapshot
}

const LocalizationContext = createContext<LocalizationContextValue | undefined>(undefined)

export interface LocalizationProviderProps {
  localization: Localization
  children: ReactNode
  fallback?: ReactNode
}

export function LocalizationProvider({ localization, children, fallback = null }: LocalizationProviderProps) {
  const snapshot = useSyncExternalStore(
    localization.subscribe,
    () => localization.snapshot,
    () => localization.snapshot
  )
  const context = useMemo(() => ({ localization, snapshot }), [localization, snapshot])
  if (snapshot.status === "failed") throw snapshot.error
  if (snapshot.status === "loading") return fallback
  return (
    <LocalizationContext.Provider value={context}>
      <I18nextProvider i18n={localization.i18n}>{children}</I18nextProvider>
    </LocalizationContext.Provider>
  )
}

export function useOptionalLocalization(): Localization | undefined {
  return useContext(LocalizationContext)?.localization
}

export function useLocalization(): Localization {
  const localization = useOptionalLocalization()
  if (!localization) throw new Error("useLocalization must be used within LocalizationProvider or ApplicationProvider.")
  return localization
}

export function useCulture(): LocalizationSettings {
  return useContext(LocalizationContext)?.snapshot ?? defaultLocalizationSettings
}

export function useFormatting(): LocalizationFormatters {
  const { culture, uiCulture, timeZone } = useCulture()
  return useMemo(() => createFormatting({ culture, uiCulture, timeZone }), [culture, uiCulture, timeZone])
}

/** Opt-in only: embedded applications must not change the host document's language. */
export function LocalizationDocument() {
  const localization = useLocalization()
  const { uiCulture } = useCulture()
  const direction = localization.i18n.dir(uiCulture)
  useEffect(() => {
    const root = document.documentElement
    const previousLanguage = root.getAttribute("lang")
    const previousDirection = root.getAttribute("dir")
    root.setAttribute("lang", uiCulture)
    root.setAttribute("dir", direction)
    return () => {
      if (root.getAttribute("lang") === uiCulture) {
        if (previousLanguage === null) root.removeAttribute("lang")
        else root.setAttribute("lang", previousLanguage)
      }
      if (root.getAttribute("dir") === direction) {
        if (previousDirection === null) root.removeAttribute("dir")
        else root.setAttribute("dir", previousDirection)
      }
    }
  }, [uiCulture, direction])
  return null
}
